import { useEffect, useState } from 'react'
import { KNOWLEDGE_TOPICS, type KnowledgeTopicId } from '@k4ia/shared'
import {
  createKnowledgeText,
  getAnalytics,
  getConversation,
  listConversations,
  resolveKnowledgeGap,
  type AdminAnalyticsSummary,
  type AdminConversationMessage,
  type AdminConversationSession,
} from '../lib/api'
import { Sheet } from '../components/ui'

const RANGES = [7, 30, 90] as const

/**
 * SPEC "Analytics": Sitzungen pro Tag, beliebteste Showcases, häufigste
 * Fragen, Wissenslücken, Bewertungen, Nutzung — plus die anonymisierten
 * Verläufe ("Gespräche"). Alle Daten kommen aus dem D1-Spiegel, der nach der
 * konfigurierten Löschfrist automatisch geleert wird (siehe worker.ts).
 */
export function AnalyticsPage() {
  const [days, setDays] = useState<number>(30)
  const [summary, setSummary] = useState<AdminAnalyticsSummary | null>(null)
  const [sessions, setSessions] = useState<AdminConversationSession[]>([])
  const [openSession, setOpenSession] = useState<{
    session: AdminConversationSession
    messages: AdminConversationMessage[]
  } | null>(null)
  /** Die Wissenslücke, für die gerade ein Eintrag geschrieben wird. */
  const [offeneLuecke, setOffeneLuecke] = useState<string | null>(null)

  useEffect(() => {
    void getAnalytics(days).then((r) => setSummary(r.summary))
  }, [days])

  useEffect(() => {
    void listConversations().then((r) => setSessions(r.sessions))
  }, [])

  async function handleResolveGap(id: string) {
    await resolveKnowledgeGap(id)
    const r = await getAnalytics(days)
    setSummary(r.summary)
  }

  if (!summary) return <p className="text-text-muted">Lädt …</p>

  const maxSessions = Math.max(1, ...summary.sessionsPerDay.map((d) => d.count))

  return (
    <div className="max-w-4xl space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="page-title">Analytics</h2>
        <div
          className="flex w-full rounded-full bg-surface-2 p-1 sm:w-auto"
          role="group"
          aria-label="Zeitraum"
        >
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              aria-pressed={days === r}
              className={`min-h-[2.25rem] flex-1 rounded-full px-4 text-sm font-medium transition-colors sm:flex-none ${
                days === r ? 'bg-accent text-on-accent' : 'text-text-muted'
              }`}
            >
              {r} Tage
            </button>
          ))}
        </div>
      </header>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Sitzungen" value={String(summary.usage.sessions)} />
        <Stat label="Antworten" value={String(summary.usage.assistantMessages)} />
        <Stat
          label="Ø Antwortzeit"
          value={summary.usage.avgLatencyMs === null ? '—' : `${summary.usage.avgLatencyMs} ms`}
        />
        <Stat
          label="Allgemeinwissen"
          value={`${Math.round(summary.usage.generalKnowledgeShare * 100)} %`}
          hint="Anteil der Antworten ohne Treffer in der Wissensbasis"
        />
      </section>

      <Section title="Kosten">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat
            label="Heute"
            value={eur(summary.cost.todayEur)}
            hint={`Tagesgrenze ${eur(summary.cost.limitPerDayEur)}`}
          />
          <Stat
            label="Grenze je Gespräch"
            value={eur(summary.cost.limitPerSessionEur)}
            hint="Danach verweist K4ia ans Team"
          />
          <Stat
            label="Abgewiesen heute"
            value={String(summary.cost.limitedTurnsToday)}
            hint="Züge, die eine Kostengrenze gestoppt hat"
          />
        </div>
        <p className="mt-3 text-xs text-text-muted">
          Schätzung aus Tokenzahlen mal hinterlegtem Preis (Konfiguration), keine Abrechnung — die
          gilt beim Anbieter.
        </p>
        {summary.cost.perDay.length > 0 && (
          <ul className="mt-3 space-y-1">
            {summary.cost.perDay.slice(0, 14).map((d) => (
              <li key={d.day} className="flex flex-wrap items-baseline gap-x-3 text-sm">
                <span className="w-24 shrink-0 text-text-muted">{d.day}</span>
                <span className="w-20 text-right tabular-nums">{eur(d.costEur)}</span>
                <span className="text-xs text-text-muted max-sm:w-full max-sm:pl-[6.75rem]">
                  {d.tokensIn.toLocaleString('de-DE')} ein / {d.tokensOut.toLocaleString('de-DE')}{' '}
                  aus
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Sitzungen pro Tag">
        {summary.sessionsPerDay.length === 0 ? (
          <Empty />
        ) : (
          <ul className="space-y-1">
            {summary.sessionsPerDay.map((d) => (
              <li key={d.day} className="flex items-center gap-3 text-sm">
                <span className="w-24 shrink-0 text-text-muted">{d.day}</span>
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span
                    className="h-3 rounded-sm bg-accent"
                    style={{ width: `${(d.count / maxSessions) * 100}%`, minWidth: '0.5rem' }}
                  />
                  <span className="shrink-0 tabular-nums text-text-muted">{d.count}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Beliebteste Showcases">
        {summary.topShowcases.length === 0 ? (
          <Empty />
        ) : (
          <ol className="space-y-1 text-sm">
            {summary.topShowcases.map((s) => (
              <li key={s.showcaseId} className="flex justify-between gap-4">
                <span className="min-w-0">{s.title}</span>
                <span className="shrink-0 text-text-muted">{s.visits} Besuche</span>
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section title="Häufigste Fragen">
        {summary.topQuestions.length === 0 ? (
          <Empty />
        ) : (
          <ol className="space-y-1 text-sm">
            {summary.topQuestions.map((q) => (
              <li key={q.question} className="flex justify-between gap-4">
                <span className="truncate">{q.question}</span>
                <span className="shrink-0 text-text-muted">{q.count}×</span>
              </li>
            ))}
          </ol>
        )}
      </Section>

      <Section
        title="Wissenslücken"
        hint="Fragen, die K4ia nicht aus den eigenen Inhalten beantworten konnte — hier fehlt Content."
      >
        {summary.knowledgeGaps.length === 0 ? (
          <Empty />
        ) : (
          <ul className="card divide-y divide-border overflow-hidden text-sm">
            {summary.knowledgeGaps.map((g) => (
              <li key={g.id} className="space-y-3 px-4 py-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="min-w-0 sm:truncate">{g.question}</span>
                    <span className="shrink-0 text-xs text-text-muted">{g.occurrenceCount}×</span>
                  </span>
                  <span className="flex shrink-0 gap-2">
                    <button
                      onClick={() => setOffeneLuecke(offeneLuecke === g.id ? null : g.id)}
                      aria-expanded={offeneLuecke === g.id}
                      className="btn-chip"
                    >
                      Als Wissen anlegen
                    </button>
                    <button onClick={() => void handleResolveGap(g.id)} className="btn-chip">
                      Erledigt
                    </button>
                  </span>
                </div>
                {offeneLuecke === g.id && (
                  <GapToKnowledgeForm
                    question={g.question}
                    onDone={async () => {
                      setOffeneLuecke(null)
                      await handleResolveGap(g.id)
                    }}
                    onCancel={() => setOffeneLuecke(null)}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Bewertungen">
        <p className="text-sm">
          👍 {summary.feedback.up} · 👎 {summary.feedback.down}
        </p>
      </Section>

      <Section
        title="Gespräche"
        hint="Anonymisierte Verläufe (Sitzungs-IDs sind Zufalls-UUIDs ohne Personenbezug); werden nach der konfigurierten Löschfrist automatisch gelöscht."
      >
        {sessions.length === 0 ? (
          <Empty />
        ) : (
          <ul className="card divide-y divide-border overflow-hidden text-sm">
            {sessions.slice(0, 15).map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => void getConversation(s.id).then(setOpenSession)}
                  className="flex min-h-[3rem] w-full items-center justify-between gap-4 px-4 text-left active:bg-surface-2"
                >
                  <span className="truncate text-text-muted">
                    {s.startedAt.slice(0, 16).replace('T', ' ')} · {s.channel}
                  </span>
                  <span className="shrink-0 font-medium text-brand">Ansehen</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {openSession && (
          <Sheet
            title={`Verlauf ${openSession.session.id.slice(0, 8)}… · ${openSession.session.channel}`}
            onClose={() => setOpenSession(null)}
          >
            <ol className="space-y-3 text-sm">
              {openSession.messages.map((m) => (
                <li
                  key={m.id}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 ${
                    m.role === 'user' ? 'ml-auto bg-accent text-on-accent' : 'bg-surface-2'
                  }`}
                >
                  <span className="whitespace-pre-wrap">{m.content}</span>
                  {m.generalKnowledge && (
                    <span className="mt-1 block text-xs text-status-pending">Allgemeinwissen</span>
                  )}
                </li>
              ))}
            </ol>
          </Sheet>
        )}
      </Section>
    </div>
  )
}

/**
 * Aus einer Wissenslücke ein Wissens-Eintrag: Die Frage steht als Titel, die
 * Antwort tippt man hier — sie landet als Eintrag in der Wissensbasis, und
 * die Lücke gilt als erledigt. Der kürzeste Weg von „konnte K4ia nicht
 * beantworten" zu „kann sie jetzt".
 */
function GapToKnowledgeForm({
  question,
  onDone,
  onCancel,
}: {
  question: string
  onDone: () => Promise<void>
  onCancel: () => void
}) {
  const [title, setTitle] = useState(question.charAt(0).toUpperCase() + question.slice(1))
  const [topic, setTopic] = useState<KnowledgeTopicId>('sonstiges')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await createKnowledgeText({ title: title.trim(), topic, body: body.trim() })
      await onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2 rounded-xl bg-surface-2 p-3">
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <input
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titel des Eintrags"
        />
        <select
          className="input sm:w-auto"
          value={topic}
          onChange={(e) => setTopic(e.target.value as KnowledgeTopicId)}
        >
          {KNOWLEDGE_TOPICS.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="input min-h-[6rem]"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Die Antwort, wie K4ia sie geben soll — in ganzen Sätzen, mit allem, was dazugehört."
      />
      {error && <p className="text-xs text-status-error">{error}</p>}
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          className="btn-primary"
          disabled={busy || !title.trim() || !body.trim()}
          onClick={() => void save()}
        >
          {busy ? 'Speichert …' : 'Eintrag anlegen und Lücke schließen'}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Abbrechen
        </button>
      </div>
    </div>
  )
}

function Section({
  title,
  hint,
  children,
}: {
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section>
      <h3 className="mb-2 text-lg">{title}</h3>
      {hint && <p className="mb-2 text-xs text-text-muted">{hint}</p>}
      {children}
    </section>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl bg-surface-2 p-4" title={hint}>
      <div className="text-xs text-text-muted">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {/* Am Handy gibt es kein Hover — der Hinweis steht deshalb klein darunter. */}
      {hint && <div className="mt-0.5 text-[0.7rem] leading-snug text-text-muted">{hint}</div>}
    </div>
  )
}

function Empty() {
  return <p className="text-sm text-text-muted">Noch keine Daten in diesem Zeitraum.</p>
}

/** Kleine Beträge sind hier der Normalfall — zwei Nachkommastellen reichen nicht. */
function eur(value: number): string {
  const digits = value > 0 && value < 0.1 ? 4 : 2
  return `${value.toLocaleString('de-DE', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} €`
}
